import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Book, Wrench, FileText, Layers, Search, X, Compass } from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import SEO from "@/components/SEO";
import { useProducts } from "@/lib/ProductsContext";
import { getGeneratedArticles } from "@/lib/api";
import { generateTags, TAG_LABELS } from "@/lib/articleTags";

const CATEGORY_FILTERS = [
  { id: "all", label: "All" },
  { id: "side-effects", label: "Side Effects" },
  { id: "food-protein", label: "Food" },
  { id: "expectations", label: "Expectations" },
  { id: "results", label: "Results" },
];

// Topic chip filters for the Free Articles grid — keyword-matched against
// slug + title + keyword + content text. Lets users scan 165+ articles by topic.
const TOPIC_FILTERS = [
  { id: "all", label: "All topics", match: () => true },
  { id: "side-effects", label: "Side Effects", keywords: ["nausea", "constipation", "fatigue", "headache", "heartburn", "side effect", "side-effect", "vomit", "diarrhea", "burp", "sulfur"] },
  { id: "nutrition", label: "Nutrition", keywords: ["protein", "food", "eat", "meal", "snack", "calorie", "diet", "carb", "fiber", "electrolyte", "hydrat", "water"] },
  { id: "plateau", label: "Plateau", keywords: ["plateau", "stalled", "stuck", "stop losing", "not losing", "slow", "stall"] },
  { id: "hair-skin", label: "Hair & Skin", keywords: ["hair", "skin", "snail mucin", "dry skin", "ozempic face", "elasticity", "glow", "barrier"] },
  { id: "muscle", label: "Muscle", keywords: ["muscle", "strength", "lean", "lift", "training", "exercise"] },
  { id: "dose", label: "Dose & Meds", keywords: ["dose", "dosage", "titration", "compounded", "wegovy", "ozempic", "mounjaro", "zepbound", "semaglutide", "tirzepatide", "injection", "shot", "pen"] },
  { id: "energy", label: "Energy", keywords: ["energy", "fatigue", "tired", "sleep", "exhausted", "afternoon"] },
  { id: "longterm", label: "Long-term", keywords: ["maintenance", "stop", "off ", "rebound", "after", "long-term", "long term", "weaning", "cycling"] },
];

// Build a hub lookup: slug → hub id (built inside component from context)

const START_HERE = [
  {
    title: "New to GLP-1",
    description: "Everything you need for your first 30 days",
    slug: "ozempic-first-week-guide",
    color: "bg-[#2D6A4F]",
  },
  {
    title: "Managing Side Effects",
    description: "Nausea, fatigue, constipation and more",
    slug: "how-to-stop-nausea-on-semaglutide",
    color: "bg-[#E07A5F]",
  },
  {
    title: "What to Eat",
    description: "Meals, snacks, and timing that work",
    slug: "best-foods-for-ozempic-users",
    color: "bg-[#D4A373]",
  },
  {
    title: "Weight Loss Help",
    description: "Plateaus, slow progress, and what to change",
    slug: "why-not-losing-weight-on-semaglutide",
    color: "bg-[#6B705C]",
  },
];

const HubPage = () => {
  const { guides: GUIDES, bundle: BUNDLE, tools: TOOLS } = useProducts();
  const { seoContent: SEO_CONTENT, articleHubs: ARTICLE_HUBS } = useContent();

  // Build hub lookup inside component
  const articleHubMap = {};
  Object.entries(ARTICLE_HUBS).forEach(([hubId, hub]) => {
    (hub.articles || []).forEach(slug => { articleHubMap[slug] = hubId; });
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [dynamicArticles, setDynamicArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [activeFilter, setActiveFilter] = useState(searchParams.get("tag") ? "all" : "all");
  const [activeTag, setActiveTag] = useState(searchParams.get("tag") || "");
  const [activeTopic, setActiveTopic] = useState("all");
  const staticPages = Object.values(SEO_CONTENT);

  useEffect(() => {
    getGeneratedArticles().then(res => setDynamicArticles(res.data.articles || [])).catch(() => {});
  }, []);

  const allArticles = useMemo(() => {
    return [...staticPages, ...dynamicArticles];
  }, [staticPages, dynamicArticles]);

  // Topic-filtered list for the All Articles grid.
  const topicFilteredArticles = useMemo(() => {
    if (activeTopic === "all") return allArticles;
    const t = TOPIC_FILTERS.find((x) => x.id === activeTopic);
    if (!t || !t.keywords) return allArticles;
    const kws = t.keywords.map((k) => k.toLowerCase());
    return allArticles.filter((p) => {
      const haystack = (
        (p.slug || "") + " " +
        (p.title || "") + " " +
        (p.keyword || "") + " " +
        (p.metaDescription || "") + " " +
        (p.content?.intro || p.content?.hook || "")
      ).toLowerCase();
      return kws.some((k) => haystack.includes(k));
    });
  }, [allArticles, activeTopic]);

  // Per-topic counts for the chip badges (computed once)
  const topicCounts = useMemo(() => {
    const counts = { all: allArticles.length };
    for (const t of TOPIC_FILTERS) {
      if (t.id === "all") continue;
      const kws = (t.keywords || []).map((k) => k.toLowerCase());
      counts[t.id] = allArticles.filter((p) => {
        const haystack = (
          (p.slug || "") + " " + (p.title || "") + " " + (p.keyword || "")
        ).toLowerCase();
        return kws.some((k) => haystack.includes(k));
      }).length;
    }
    return counts;
  }, [allArticles]);

  const filteredArticles = useMemo(() => {
    let results = allArticles;

    // Category filter
    if (activeFilter !== "all") {
      results = results.filter(page => {
        const hub = page.hub || articleHubMap[page.slug];
        return hub === activeFilter;
      });
    }

    // Tag filter (from URL param or tag click)
    if (activeTag) {
      results = results.filter(page => {
        const tags = generateTags(page);
        return tags.includes(activeTag);
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      results = results.filter(page => {
        const title = (page.title || "").toLowerCase();
        const keyword = (page.keyword || "").toLowerCase();
        const desc = (page.metaDescription || "").toLowerCase();
        const hook = (page.content?.hook || "").toLowerCase();
        const intro = (page.content?.intro || "").toLowerCase();
        const hub = page.hub || articleHubMap[page.slug] || "";
        return title.includes(q) || keyword.includes(q) || desc.includes(q) || hook.includes(q) || intro.includes(q) || hub.includes(q);
      });
    }

    return results;
  }, [allArticles, searchQuery, activeFilter, activeTag]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAll = () => {
    setSearchQuery("");
    setActiveFilter("all");
    setActiveTag("");
    setSearchParams({});
  };

  const isFiltering = searchQuery || activeFilter !== "all" || activeTag;

  const hubs = Object.values(ARTICLE_HUBS);

  return (
    <div className="noise-bg">
      <SEO title="GLP-1 Resource Hub — 90+ Free Articles" description="Browse 90+ educational articles about GLP-1 medications. Side effects, nutrition, weight loss plateaus, and more. Free and updated weekly." />
      {/* Header */}
      <section className="gradient-hero section-padding">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="mb-6" data-testid="hub-title">
                Complete GLP-1 Side Effect and Routine System
              </h1>
              <p className="text-xl text-[#4B5548]">
                Everything you need to thrive on Ozempic, Wegovy, Mounjaro, and other GLP-1 medications.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sticky Search Bar */}
      <section className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
        <div className="container-main py-3">
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#838F80]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search GLP articles..."
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30 focus:border-[#2D6A4F] transition-all"
                data-testid="article-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#838F80] hover:text-[#1A2018]"
                  data-testid="search-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Quick Filters */}
            <div className="flex gap-2 flex-wrap justify-center sm:justify-start">
              {CATEGORY_FILTERS.map(f => (
                <button
                  key={f.id}
                  onClick={() => { setActiveFilter(f.id); setActiveTag(""); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeFilter === f.id && !activeTag
                      ? "bg-[#2D6A4F] text-white"
                      : "bg-[#E9EDC9] text-[#4B5548] hover:bg-[#2D6A4F]/10"
                  }`}
                  data-testid={`filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
              {activeTag && (
                <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#2D6A4F] text-white flex items-center gap-1">
                  {TAG_LABELS[activeTag] || activeTag}
                  <button onClick={() => { setActiveTag(""); setSearchParams({}); }} className="ml-1 hover:text-white/70">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Nav Links (shown only when no search active) */}
      {!isFiltering && (
        <section className="py-4 bg-white border-b border-gray-50">
          <div className="container-main">
            <div className="flex flex-wrap justify-center gap-3">
              <a href="#start-here" className="px-4 py-2 bg-[#E9EDC9] rounded-full text-sm font-medium text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white transition-colors">
                Start Here
              </a>
              <a href="#tools" className="px-4 py-2 bg-[#E9EDC9] rounded-full text-sm font-medium text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white transition-colors">
                Free Tools
              </a>
              <a href="#hubs" className="px-4 py-2 bg-[#E9EDC9] rounded-full text-sm font-medium text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white transition-colors">
                Topic Hubs
              </a>
              <a href="#guides" className="px-4 py-2 bg-[#E9EDC9] rounded-full text-sm font-medium text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white transition-colors">
                Guides
              </a>
              <a href="#articles" className="px-4 py-2 bg-[#E9EDC9] rounded-full text-sm font-medium text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white transition-colors">
                Articles
              </a>
              <Link to="/glp-survival-system" className="px-4 py-2 bg-[#2D6A4F] rounded-full text-sm font-medium text-white hover:bg-[#1B4332] transition-colors">
                Get Complete System
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Search Results Mode */}
      {isFiltering ? (
        <section className="section-padding bg-[#F2F0E9]">
          <div className="container-main">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#4B5548]" data-testid="search-results-count">
                {filteredArticles.length} article{filteredArticles.length !== 1 ? "s" : ""} found
                {searchQuery && <span> for "<strong>{searchQuery}</strong>"</span>}
                {activeFilter !== "all" && <span> in <strong>{CATEGORY_FILTERS.find(f => f.id === activeFilter)?.label}</strong></span>}
                {activeTag && <span> tagged <strong>{TAG_LABELS[activeTag] || activeTag}</strong></span>}
              </p>
              <button
                onClick={clearAll}
                className="text-sm text-[#2D6A4F] font-medium hover:underline"
                data-testid="clear-all-filters"
              >
                Clear all
              </button>
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-16" data-testid="no-results-message">
                <p className="text-lg text-[#4B5548] mb-2">No results found.</p>
                <p className="text-sm text-[#838F80]">Try a different keyword.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredArticles.map((page, index) => {
                  const hubId = page.hub || articleHubMap[page.slug];
                  const hubLabel = hubId ? ARTICLE_HUBS[hubId]?.shortTitle : null;
                  return (
                    <motion.div
                      key={page.slug}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                    >
                      <Link
                        to={`/articles/${page.slug}`}
                        className="card-elevated p-5 block h-full hover:border-[#2D6A4F] border border-transparent transition-all"
                        data-testid={`search-result-${page.slug}`}
                      >
                        {hubLabel && (
                          <span className="inline-block px-2 py-0.5 bg-[#E9EDC9] text-[#2D6A4F] rounded-full text-[10px] font-medium mb-2">
                            {hubLabel}
                          </span>
                        )}
                        <h3 className="font-semibold text-[#1A2018] leading-tight mb-2">
                          {page.title}
                        </h3>
                        <p className="text-xs text-[#4B5548] line-clamp-2">
                          {(page.content?.hook || page.content?.intro || "").slice(0, 120)}...
                        </p>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      ) : (
        <>
          {/* Start Here Section */}
          <section className="section-padding bg-white" id="start-here">
            <div className="container-main">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 mb-2"
              >
                <div className="w-10 h-10 rounded-full bg-[#2D6A4F] flex items-center justify-center">
                  <Compass className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl">Start Here</h2>
              </motion.div>
              <p className="text-sm text-[#4B5548] mb-6 ml-[52px]">Not sure where to begin? Pick what matches your situation.</p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {START_HERE.map((item, index) => (
                  <motion.div
                    key={item.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08 }}
                  >
                    <Link
                      to={`/articles/${item.slug}`}
                      className="block rounded-2xl p-5 text-white hover:scale-[1.02] transition-transform"
                      style={{ backgroundColor: item.color === "bg-[#2D6A4F]" ? "#2D6A4F" : item.color === "bg-[#E07A5F]" ? "#E07A5F" : item.color === "bg-[#D4A373]" ? "#D4A373" : "#6B705C" }}
                      data-testid={`start-here-${item.slug}`}
                    >
                      <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-white/80">{item.description}</p>
                      <ArrowRight className="w-4 h-4 mt-3 text-white/60" />
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Free Tools Section */}
          <section className="section-padding bg-white" id="tools">
            <div className="container-main">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 mb-8"
              >
                <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-[#2D6A4F]" />
                </div>
                <h2 className="text-2xl">Free Tools</h2>
              </motion.div>

              <div className="grid md:grid-cols-3 gap-6">
                {Object.values(TOOLS).map((tool, index) => (
                  <motion.div
                    key={tool.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/${tool.slug}`}
                      className="card-elevated p-6 block h-full hover:border-[#2D6A4F] border-2 border-transparent"
                      data-testid={`hub-tool-${tool.id}`}
                    >
                      <span className="inline-block px-3 py-1 bg-[#E9EDC9] text-[#2D6A4F] rounded-full text-xs font-medium mb-3">
                        Free
                      </span>
                      <h3 className="text-xl font-semibold mb-2 text-[#1A2018]">{tool.title}</h3>
                      <p className="text-sm text-[#4B5548] mb-4">{tool.description}</p>
                      <span className="text-[#2D6A4F] font-medium text-sm flex items-center gap-1">
                        Try it now <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Topic Hubs Section */}
          <section className="section-padding bg-[#F2F0E9]" id="hubs">
            <div className="container-main">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 mb-8"
              >
                <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                  <Layers className="w-5 h-5 text-[#2D6A4F]" />
                </div>
                <h2 className="text-2xl">Browse by Topic</h2>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {hubs.map((hub, index) => (
                  <motion.div
                    key={hub.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/${hub.slug}`}
                      className="card-elevated p-6 block h-full hover:border-[#2D6A4F] border-2 border-transparent transition-all"
                      data-testid={`hub-topic-${hub.id}`}
                    >
                      <h3 className="text-lg font-semibold mb-2 text-[#1A2018]">{hub.shortTitle}</h3>
                      <p className="text-sm text-[#4B5548] mb-3 line-clamp-2">{hub.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#838F80]">{hub.articles.length} articles</span>
                        <ArrowRight className="w-4 h-4 text-[#2D6A4F]" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Guides Section */}
          <section className="section-padding bg-white" id="guides">
            <div className="container-main">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center justify-between mb-8"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                    <Book className="w-5 h-5 text-[#2D6A4F]" />
                  </div>
                  <h2 className="text-2xl">Guides</h2>
                </div>
                <Link
                  to="/glp-survival-system"
                  className="btn-primary text-sm py-2 px-4"
                  data-testid="hub-bundle-btn"
                >
                  Get All 8 for ${BUNDLE?.price || 49}
                </Link>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.values(GUIDES).map((guide, index) => (
                  <motion.div
                    key={guide.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={`/guides/${guide.slug}`}
                      className="card-elevated p-4 block h-full"
                      data-testid={`hub-guide-${guide.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-semibold ${
                          guide.category === "core" ? "text-[#E07A5F]" : "text-[#2D6A4F]"
                        }`}>
                          ${guide.price}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-[#1A2018] leading-tight">{guide.title}</h3>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* All Articles Section */}
          <section className="section-padding bg-[#F2F0E9]" id="articles">
            <div className="container-main">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex items-center gap-3 mb-4"
              >
                <div className="w-10 h-10 rounded-full bg-[#E9EDC9] flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#2D6A4F]" />
                </div>
                <h2 className="text-2xl">
                  Free Articles
                  <span className="text-[#838F80] text-base font-normal ml-2">
                    ({topicFilteredArticles.length}{activeTopic !== "all" ? ` of ${allArticles.length}` : ""})
                  </span>
                </h2>
              </motion.div>

              {/* Topic filter chips */}
              <div className="flex flex-wrap gap-2 mb-8" data-testid="hub-topic-filter">
                {TOPIC_FILTERS.map((t) => {
                  const count = topicCounts[t.id] || 0;
                  const active = activeTopic === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActiveTopic(t.id)}
                      disabled={t.id !== "all" && count === 0}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        active
                          ? "bg-[#2D6A4F] text-white"
                          : count === 0
                            ? "bg-white/60 text-[#838F80] border border-[#E8E4DC] cursor-not-allowed"
                            : "bg-white text-[#4B5548] border border-[#E8E4DC] hover:border-[#2D6A4F] hover:text-[#1A2018]"
                      }`}
                      data-testid={`hub-topic-${t.id}`}
                    >
                      {t.label}
                      <span className={`text-[11px] ${active ? "opacity-90" : "opacity-70"}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {topicFilteredArticles.length === 0 ? (
                <div className="text-center py-12 text-[#4B5548]">
                  <p className="text-sm">No articles match this topic yet.</p>
                  <button
                    onClick={() => setActiveTopic("all")}
                    className="mt-3 text-sm text-[#2D6A4F] underline"
                    data-testid="hub-topic-clear"
                  >
                    Show all
                  </button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {topicFilteredArticles.map((page, index) => (
                    <motion.div
                      key={page.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: Math.min(index * 0.02, 0.3) }}
                    >
                      <Link
                        to={`/articles/${page.slug}`}
                        className="card-elevated p-5 block h-full hover:border-[#2D6A4F] border border-transparent transition-all"
                        data-testid={`hub-article-${page.slug}`}
                      >
                        <h3 className="font-semibold text-[#1A2018] leading-tight mb-2">
                          {page.title}
                        </h3>
                        <p className="text-xs text-[#4B5548] line-clamp-2">
                          {(page.content?.hook || page.content?.intro || "").slice(0, 120)}...
                        </p>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Bundle CTA */}
      <section className="section-padding gradient-bundle text-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge-best-value mb-4 inline-block">Save 60%+</span>
              <h2 className="text-white mb-4">Get the Complete GLP Survival System</h2>
              <p className="text-lg text-white/80 mb-8">
                All 8 educational guides for just ${BUNDLE?.price || 49} (normally ${BUNDLE?.original_value || 127}).
                Manage side effects, build routines, and support your GLP-1 journey.
              </p>
              <Link
                to="/glp-survival-system"
                className="btn-coral inline-flex"
                data-testid="hub-final-cta"
              >
                Get the Complete System
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HubPage;
